import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { useToast } from "@/shared/hooks/use-toast";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { useTitleAvailability } from "../hooks/useTitleAvailability";
import { useIntegrationBaseCache } from "../hooks/useIntegrationBaseCache";
import { IntegrationScopeField } from "./IntegrationScopeField";
import type { IntegrationBase } from "../types/integration";

interface IntegrationGeneralTabProps {
  integration: IntegrationBase;
}

/**
 * Aba Geral (AB#87542): o title — que identifica a instalação e é único na organização —
 * e o escopo. Mesma regra de escopo do diálogo de criar: sem workspace selecionado, não há
 * para onde restringir, e o campo vira só leitura.
 */
export const IntegrationGeneralTab = ({ integration }: IntegrationGeneralTabProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const { scope: viewScope } = useViewScope();
  const isOrgView = viewScope === "organization";
  const applyUpdated = useIntegrationBaseCache();
  const titleAvailability = useTitleAvailability(integration.id);

  const savedTitle = integration.title || integration.label || "";
  const savedGlobal = integration.scope !== "local";
  const [title, setTitle] = useState(savedTitle);
  const [isGlobal, setIsGlobal] = useState(savedGlobal);
  const [isSaving, setIsSaving] = useState(false);

  // A instância mudou por fora (outra aba gravou): o formulário volta ao gravado.
  useEffect(() => {
    setTitle(savedTitle);
    setIsGlobal(savedGlobal);
  }, [savedTitle, savedGlobal]);

  const dirty = title.trim() !== savedTitle || isGlobal !== savedGlobal;

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ title: t("common.error"), description: t("integrations.form.title.required"), variant: "destructive" });
      return;
    }
    if (titleAvailability.status === "taken") {
      toast({ title: t("common.error"), description: t("integrations.form.title.taken"), variant: "destructive" });
      return;
    }
    if (!isGlobal && !currentWorkspace) {
      toast({ title: t("common.error"), description: t("integrations.scope.missingWorkspace"), variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const result = await IntegrationBaseService.update(integration.id, {
        title: title.trim(),
        scope: isGlobal ? "global" : "local",
        // product_id só no escopo do workspace: é ele que cria o vínculo na troca de escopo.
        product_id: !isGlobal && currentWorkspace ? currentWorkspace.id : undefined,
      });
      await applyUpdated(result.integration, integration.id);
      toast({ title: t("integrations.messages.saved") });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="max-w-xl space-y-6 pt-6">
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="integration-title">
            {t("integrations.form.title.label")}
            <span className="ml-1 text-destructive">*</span>
          </label>
          <Input
            id="integration-title"
            value={title}
            placeholder={t("integrations.form.title.placeholder")}
            onChange={(e) => {
              setTitle(e.target.value);
              titleAvailability.reset();
            }}
            onBlur={() => {
              if (title.trim() !== savedTitle) titleAvailability.check(title);
            }}
            aria-invalid={titleAvailability.status === "taken"}
          />
          {titleAvailability.status === "taken" ? (
            <p className="text-xs text-destructive">{t("integrations.form.title.taken")}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{t("integrations.form.title.hint")}</p>
          )}
        </div>

        {isOrgView ? (
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-muted-foreground">{t("integrations.scope.permissionsLabel")}</h3>
            <p className="text-sm">
              {savedGlobal ? t("integrations.scope.organizationLabel") : t("integrations.scope.productLabel")}
            </p>
          </div>
        ) : (
          <IntegrationScopeField isGlobal={isGlobal} onChange={setIsGlobal} name="detail-scope" bare />
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!dirty || isSaving}>
            {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("integrations.actions.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
